import React from 'react';
import { ArrowRight, ShieldAlert, FileText, Cpu, AlertTriangle } from 'lucide-react';

interface LandingViewProps {
  onStart: () => void;
}

export const LandingView: React.FC<LandingViewProps> = ({ onStart }) => {
  return (
    <div className="min-h-[100dvh] flex flex-col justify-between px-5 py-7 max-w-md mx-auto relative overflow-hidden bg-paper-50 text-ink-900 select-none">
      {/* 顶部机密研究所标识 */}
      <header className="relative z-10 border-b-2 border-ink-900 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 bg-stamp animate-pulse rounded-xs" />
          <span className="font-mono text-xs font-bold tracking-wider uppercase text-ink-900">
            SYS.LAB-904 // 异常生物收容与配对所
          </span>
        </div>
        <span className="text-[10px] font-mono border border-ink-800 px-1.5 py-0.5 rounded-xs font-semibold bg-paper-100 text-ink-700">
          密级：机密
        </span>
      </header>

      {/* 主视觉与文案 */}
      <main className="relative z-10 my-auto py-5 space-y-6">
        {/* 档案编号与印章 */}
        <div className="flex items-center justify-between">
          <span className="font-mono text-[11px] text-ink-500 tracking-widest">
            DOSSIER NO. 2026-X77
          </span>
          <span className="inline-block px-2 py-0.5 border border-stamp text-stamp text-[10px] font-mono font-black uppercase tracking-wider rotate-[-2deg]">
            EXPERIMENTAL // 实验性评估
          </span>
        </div>

        {/* 核心主标题 */}
        <div className="space-y-3">
          <div className="inline-flex items-center gap-1.5 text-xs font-mono font-bold text-hazard-dark bg-hazard-light/70 px-2 py-1 border border-hazard-dark/30">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span>伪正经生活状态测定程序</span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-black text-ink-900 tracking-tight leading-[1.18]">
            测测你这辈子
            <br />
            <span className="relative inline-block mt-1">
              <span className="relative z-10 bg-ink-900 text-paper-50 px-2 py-0.5">
                到底适合养什么东西
              </span>
            </span>
          </h1>

          <p className="text-sm font-medium text-ink-600 leading-relaxed pt-1 border-l-2 border-ink-300 pl-3">
            基于你的生活习惯、精神状态和一些我们也无法解释的因素。
          </p>
        </div>

        {/* 严谨实验室风格说明卡片 */}
        <div className="p-4 bg-paper-100 border border-ink-300 shadow-sharp-sm space-y-2.5 text-[13px] text-ink-800">
          <div className="flex items-center gap-1.5 font-bold text-ink-900 font-mono text-xs pb-1.5 border-b border-ink-200">
            <FileText className="w-3.5 h-3.5" />
            <span>【受试者事前知情申明】</span>
          </div>
          <p className="leading-relaxed">
            1. 本测试共包含 <strong className="text-ink-950 font-mono">35 道</strong> 递进式生活与精神测定题，测验过程不可逆。
          </p>
          <p className="leading-relaxed">
            2. 测试前半程为严肃的生活节奏与物理空间测定；中后程将不可避免地接入深层意识与不可名状物种扫描。
          </p>
          <p className="text-xs text-stamp font-medium leading-relaxed">
            * 警告：若系统最终裁定你适合饲养非自然界已知碳基物种，请不要惊慌，这往往意味着你当下的精神状态过于超前。
          </p>
          <div className="pt-2 border-t border-ink-200/70">
            <p className="text-[11px] font-mono text-ink-500 leading-normal">
              * 本测试纯属娱乐，测试结果不构成任何专业建议或现实饲养建议。
            </p>
          </div>
        </div>

        {/* 底部伪正经参数栏 */}
        <div className="grid grid-cols-3 gap-2 font-mono text-center">
          <div className="p-2 border border-ink-200 bg-white/60">
            <div className="text-[10px] text-ink-400">题目容量</div>
            <div className="text-xs font-bold text-ink-900 mt-0.5">35 阶段题</div>
          </div>
          <div className="p-2 border border-ink-200 bg-white/60">
            <div className="text-[10px] text-ink-400">平均耗时</div>
            <div className="text-xs font-bold text-ink-900 mt-0.5">~90 秒</div>
          </div>
          <div className="p-2 border border-ink-200 bg-white/60">
            <div className="text-[10px] text-ink-400">物种库谱系</div>
            <div className="text-xs font-bold text-stamp mt-0.5">碳基 / 抽象</div>
          </div>
        </div>
      </main>

      {/* 底部按钮 */}
      <footer className="relative z-10 pt-4 border-t border-ink-300/80 space-y-2">
        <button
          onClick={onStart}
          type="button"
          className="w-full py-4 px-6 bg-ink-950 hover:bg-ink-900 active:translate-x-0.5 active:translate-y-0.5 text-paper-50 font-black text-base shadow-sharp border-2 border-ink-900 transition-all flex items-center justify-center gap-2 cursor-pointer group"
        >
          <span className="tracking-widest">开始鉴定</span>
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </button>
        <p className="text-center text-[10px] font-mono text-ink-400">
          SECURE PROTOCOL V4.2 // 点击即视为自愿接受现实重塑
        </p>
      </footer>
    </div>
  );
};
